import { ChangeDetectionStrategy, ChangeDetectorRef, Component } from '@angular/core';
import { CategoryPathsModalComponent, CategoryPathsModalParams } from '@app/analyze-data/components/category-paths-modal/category-paths-modal.component';
import { COLUMN_STATS_EMPTY, CountryDropdownOption } from '@app/analyze-data/constants/analyze-data.constants';
import { CategoryPathsUtilitiesService } from '@app/analyze-data/services/category-paths-utilities.service';
import { ChannelOptionsType } from '@app/analyze-data/types/channel-options.type';
import { AmazonCategory } from '@app/category-taxonomy/models/enums/amazon-category.enum';
import { CategoryPath } from '@app/category-taxonomy/services/responses/category-paths.response';
import { isEmpty } from '@app/core/functions/isEmpty';
import { ModalService } from '@app/modules/modals/services/modal.service';
import { IconDefinition } from '@fortawesome/pro-solid-svg-icons';
import { ICellRendererAngularComp } from 'ag-grid-angular';
import { ICellRendererParams } from 'ag-grid-community';

export type CategoryPathCellType = {
    value: string;
    categoryPaths: CategoryPath[];
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type CategoryPathsCellRendererParams<T = any, V = string> = ICellRendererParams<T, V> & ICategoryPathsCellRendererParams;

export interface ICategoryPathsCellRendererParams {
    channel: ChannelOptionsType;
    country: CountryDropdownOption;
    databaseId: string;
    config: CategoryPathCellType;
    amazonCategoryType?: AmazonCategory;
    invalid?: boolean;
    onErrorIconClick?: (event: MouseEvent) => void;
}

/**
 * Use `ChangeDetectionStrategy.OnPush` for faster performance.
 * Call `this.changeDetectorRef.markForCheck();` in `agInit()` and `refresh()` methods.
 */
@Component({
    selector: 'fdx-category-paths-cell-renderer',
    templateUrl: './category-paths-cell-renderer.component.html',
    styleUrl: './category-paths-cell-renderer.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class CategoryPathsCellRendererComponent implements ICellRendererAngularComp {

    channel?: ChannelOptionsType;
    country?: CountryDropdownOption;
    databaseId?: string;
    config?: CategoryPathCellType;
    amazonCategoryType?: AmazonCategory;
    invalid?: boolean;

    shouldDisplayPathInline: boolean;
    shouldDisplayPathModal: boolean;

    readonly blankItem: string = COLUMN_STATS_EMPTY;

    private params?: ICategoryPathsCellRendererParams;

    hasErrorIconClickCallback: boolean = false;

    get displayedValue(): string {
        return this.categoryPathsUtilitiesService.getDisplayedValue(this.config);
    }

    get displayedPath(): string {
        if (isEmpty(this.config.categoryPaths)) {
            return '';
        }

        return this.categoryPathsUtilitiesService.getDisplayedPath(this.config.categoryPaths[0]);
    }

    get pathIcon(): IconDefinition {
        return this.categoryPathsUtilitiesService.pathIcon;
    }

    get errorIcon(): IconDefinition {
        return this.categoryPathsUtilitiesService.errorIcon;
    }

    constructor(
        private readonly categoryPathsUtilitiesService: CategoryPathsUtilitiesService,
        private readonly changeDetectorRef: ChangeDetectorRef,
        private readonly modalService: ModalService
    ) { }

    agInit(params: CategoryPathsCellRendererParams): void {
        this.bindParams(params);

        this.shouldDisplayPathInline = this.categoryPathsUtilitiesService.shouldDisplayPathInline(this.country, {
            category: this.config.value,
            groups: [this.config.categoryPaths]
        });
        this.shouldDisplayPathModal = this.categoryPathsUtilitiesService.shouldDisplayPathModal(this.country, {
            category: this.config.value,
            groups: [this.config.categoryPaths]
        });
        this.changeDetectorRef.markForCheck();
    }

    /**
     * Returning true tells AG Grid not to destroy and recreate the cell renderer component.
     * When returning true, you must handle changes to `params` yourself.
     * @param params cell params
     * @returns true to handle refresh logic yourself in Angular or false for AG Grid to destroy and recreate the component
     */
    refresh(params: CategoryPathsCellRendererParams): boolean {
        this.bindParams(params);
        this.changeDetectorRef.markForCheck();
        return true;
    }

    private bindParams(params: CategoryPathsCellRendererParams): void {
        this.params = params;

        this.channel = params.channel;
        this.country = params.country;
        this.databaseId = params.databaseId;
        this.config = params.config;
        this.amazonCategoryType = params.amazonCategoryType;
        this.invalid = params.invalid;

        if (this.invalid && typeof (params.onErrorIconClick) === 'function') {
            this.hasErrorIconClickCallback = true;
        }
    }

    openPathModal(): void {
        this.modalService.open(CategoryPathsModalComponent, {
            resolve: {
                channel: this.channel,
                country: this.country,
                databaseId: this.databaseId,
                categories: [this.config.categoryPaths],
                amazonCategoryType: this.amazonCategoryType
            } satisfies CategoryPathsModalParams,
            size: 'lg',
            scrollable: true
        });
    }

    errorIconClick($event: MouseEvent): void {
        if (this.hasErrorIconClickCallback) {
            this.params.onErrorIconClick($event);
        }
    }
}
